import type { EntityData, EntityKind } from "#app/lib/entity-visuals";
import type { Omittable } from "@salestrak/ui";

// Shared mention-ref vocabulary for the persisted
// span[data-type][data-id][data-label] contract. Any change to how mentions
// serialize MUST update parseMentionElement and EntityMention.renderHTML
// together, or persisted notes and the editor round-trip drift apart.

// Must stay in sync with EntityKind, or an unlisted kind's mention span is
// mis-parsed (normalized to "user").
export const KNOWN_MENTION_KINDS: readonly EntityKind[] = [
  "user",
  "team",
  "issue",
  "stage",
  "workflow",
  "issueType",
];

export type EntityMentionRef = Omittable<{
  kind: EntityKind;
  id: string;
  label: string;
  workflowId?: string;
}>;

export function normalizeMentionKind(raw: string | null): EntityKind {
  if (!raw) return "user";
  // Legacy mentions serialized people as data-type="mention".
  if (raw === "mention") return "user";
  return (KNOWN_MENTION_KINDS as readonly string[]).includes(raw)
    ? (raw as EntityKind)
    : "user";
}

export function mentionTriggerChar(kind: EntityKind): string {
  return kind === "issue" ? "#" : "@";
}

// Read-only render path is intentionally more lenient than the live editor's
// parseHTML: a bare data-id (no data-type) is still accepted as a person chip.
export function isMentionElement(el: HTMLElement): boolean {
  if (el.tagName !== "SPAN") return false;
  const hasId = !!el.getAttribute("data-id");
  const dt = el.getAttribute("data-type");
  const knownType =
    !!dt &&
    (dt === "mention" ||
      (KNOWN_MENTION_KINDS as readonly string[]).includes(dt));
  if (hasId) {
    return !dt || knownType;
  }
  return knownType;
}

export function parseMentionElement(el: HTMLElement): EntityMentionRef {
  const id = el.getAttribute("data-id") ?? "";
  return {
    kind: normalizeMentionKind(el.getAttribute("data-type")),
    id,
    label:
      el.getAttribute("data-label") ??
      el.textContent?.replace(/^[@#]/, "") ??
      id,
    workflowId: el.getAttribute("data-workflow-id") ?? undefined,
  };
}

export function issueOpenConfirm(
  entity: EntityData,
): { message: string; confirmLabel: string } | undefined {
  if (entity.kind !== "issue") return undefined;
  return {
    message:
      "Opening this project will navigate you away from what you're currently viewing.",
    confirmLabel: "Open project",
  };
}

export function fallbackMentionEntity(ref: EntityMentionRef): EntityData {
  switch (ref.kind) {
    case "user":
      return { kind: "user", id: ref.id, user: { name: ref.label } };
    case "team":
      return { kind: "team", id: ref.id, team: { teamName: ref.label } };
    case "stage":
      return {
        kind: "stage",
        id: ref.id,
        stage: { name: ref.label },
        workflowId: ref.workflowId,
      };
    case "workflow":
      return { kind: "workflow", id: ref.id, workflow: { name: ref.label } };
    case "issueType":
      return { kind: "issueType", id: ref.id, issueType: { name: ref.label } };
    case "issue":
      return {
        kind: "issue",
        id: ref.id,
        issue: { name: ref.label },
        workflowId: ref.workflowId,
      };
    case "issueSource":
      return { kind: "issueSource", id: ref.id, issueSource: { name: ref.label } };
  }
}
